import React, { useEffect, useRef, useState } from 'react';
import { PLATES, Plate } from '../../constants/plates';
import BreakdownSheet from './BreakdownSheet';

const SWATCHES = ['#5B3D63', '#7A4A2B', '#6B5B34', '#3F5A46'];

/**
 * Seconds a pill should take to cross the visible window. Speed is derived
 * from this rather than fixed in px/sec: a constant px/sec still reads as
 * fast on a phone, because the window is a third the width of a desktop's,
 * so each plate is on screen for a third as long.
 */
const CROSSING_SECONDS = 18;

const MealMarquee: React.FC = () => {
    const [selected, setSelected] = useState<Plate | null>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const windowRef = useRef<HTMLDivElement>(null);
    const [duration, setDuration] = useState(60);
    // Travel distance in px. Driving the keyframe from a measured pixel value
    // rather than -50% keeps the distance and the duration derived from the
    // same number, so they cannot disagree.
    const [shift, setShift] = useState(0);

    // Duplicated so the -50% translation loops seamlessly.
    const loop = [...PLATES, ...PLATES];

    // Derive the duration from the actual track width, and keep it correct
    // across resize and orientation changes.
    useEffect(() => {
        const track = trackRef.current;
        const viewportEl = windowRef.current;
        if (!track || !viewportEl) return;

        const measure = () => {
            // The animation travels half the track (one full set of plates).
            const distance = track.scrollWidth / 2;
            // Measure the clipping window, NOT the track's parent: the track is
            // `w-max`, so its parent shrink-wraps to the full content width and
            // would report the whole track instead of what is on screen.
            const viewport =
                viewportEl.getBoundingClientRect().width ||
                viewportEl.clientWidth ||
                window.innerWidth;
            if (distance <= 0 || viewport <= 0) return;

            // Hold each pill on screen for CROSSING_SECONDS regardless of how
            // wide the window is, then scale that to the full track length.
            const speed = viewport / CROSSING_SECONDS;
            setShift(distance);
            setDuration(distance / speed);
        };

        measure();

        // The pills size themselves from text and background images that are
        // not ready on mount, so the first measurement reads a track much
        // narrower than the final one. Re-measure once fonts and layout have
        // settled, and again on the next frames.
        const raf = requestAnimationFrame(() => requestAnimationFrame(measure));
        const timers = [80, 300, 1000].map((ms) => window.setTimeout(measure, ms));
        if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});

        const ro = new ResizeObserver(measure);
        ro.observe(track);
        ro.observe(viewportEl);
        window.addEventListener('resize', measure);

        return () => {
            cancelAnimationFrame(raf);
            timers.forEach(clearTimeout);
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    return (
        <>
            <div
                ref={windowRef}
                className="relative mt-[clamp(40px,6vw,72px)] overflow-hidden border-y border-dark-gunmetal py-5 [mask-image:linear-gradient(90deg,transparent,#000_7%,#000_93%,transparent)] [-webkit-mask-image:linear-gradient(90deg,transparent,#000_7%,#000_93%,transparent)]"
            >
                <div
                    ref={trackRef}
                    className="flex w-max motion-reduce:animate-none"
                    style={
                        shift
                            ? ({
                                  animation: `marquee-px ${duration}s linear infinite`,
                                  ['--marquee-shift' as string]: `-${shift}px`,
                              } as React.CSSProperties)
                            : undefined
                    }
                >
                    {loop.map((plate, i) => (
                        <button
                            key={`${plate.food_item}-${i}`}
                            onClick={() => setSelected(plate)}
                            aria-label={plate.food_item}
                            className="mr-3 flex items-center gap-3 rounded-full border border-dark-gunmetal bg-button-bgDark py-2 pl-2 pr-[18px] transition-[transform,border-color] duration-150 ease-out hover:-translate-y-[3px] hover:border-white/25 active:scale-[0.97]"
                        >
                            <span
                                className="h-[46px] w-[46px] shrink-0 rounded-full bg-cover bg-center"
                                style={{
                                    backgroundImage: `url(${plate.image})`,
                                    backgroundColor: SWATCHES[i % SWATCHES.length],
                                }}
                            />
                            <span className="grid gap-[3px]">
                                {/* Capped so a long name cannot stretch the pill
                                    past a phone's width. */}
                                <span className="max-w-[62vw] truncate text-left text-[14.5px] font-semibold text-white sm:max-w-none sm:whitespace-nowrap">
                                    {plate.food_item.length > 34
                                        ? `${plate.food_item.slice(0, 34)}…`
                                        : plate.food_item}
                                </span>
                                <span className="whitespace-nowrap text-left text-[10px] font-medium uppercase tracking-[0.1em] text-success">
                                    {plate.calories} · {plate.score} score
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <BreakdownSheet plate={selected} onClose={() => setSelected(null)} />
        </>
    );
};

export default MealMarquee;
